'use client';

import * as React from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '../../../tailwind/ui/popover';
import { Button } from '../../../tailwind/ui/button';
import { Checkbox } from '../../../tailwind/ui/checkbox';
import { ChevronDown, ChevronUp, Pencil, Plus } from 'lucide-react';
import { cn } from '../../../tailwind/lib/utils';

// Re-use the existing constant list so we do not duplicate data in multiple places.
// This keeps maintenance DRY – if the list changes, we update it in one place only.
// eslint-disable-next-line import/no-relative-parent-imports
import { BENCHMARKS_SELECT } from './constants';
import { IBenchmarkProps } from './interface';
import { SelectApp } from '../../../input/components/select/app';

/**
 * Custom hook for debouncing values
 */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState<T>(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * By default free users can only select these benchmarks.
 */
const FREE_BENCHMARKS = ['cdi', 'ibov'] as const;

export interface BenchmarkOption {
  label: string;
  value: string;
  /** When true the option is disabled for the current user */
  disabled?: boolean;
}

function useControlledState(
  initial: string[] | undefined,
  isSingle: boolean | undefined
) {
  const [selected, setSelected] = React.useState<string[]>(() => {
    if (initial?.length) return initial;
    return isSingle ? [BENCHMARKS_SELECT[0].value] : [];
  });

  const toggle = React.useCallback(
    (value: string) => {
      setSelected((prev) => {
        if (isSingle) return [value];
        const exists = prev.includes(value);
        return exists ? prev.filter((v) => v !== value) : [...prev, value];
      });
    },
    [isSingle]
  );

  const isSelected = React.useCallback(
    (value: string) => selected.includes(value),
    [selected]
  );

  return { selected, toggle, isSelected } as const;
}

/** DRY list item component shared by both single and multi select modes */
function BenchmarkItem({
  option,
  multiple,
  isSelected,
  onSelect,
}: {
  option: BenchmarkOption;
  multiple: boolean;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <li
      key={option.value}
      className={cn(
        'flex items-center justify-between py-1.5 cursor-pointer',
        option.disabled && 'opacity-50 pointer-events-none'
      )}
      onClick={option.disabled ? undefined : onSelect}
    >
      <div className="flex items-center gap-2">
        {multiple && (
          <Checkbox
            color="secondary"
            checked={isSelected}
            className="h-4 w-4 pointer-events-none border-secondary"
          />
        )}
        <span>{option.label}</span>
      </div>
      {/* single-select shows nothing on the right; multi-select shows nothing as well – placeholder for future icons */}
    </li>
  );
}

export const BenchmarkApp: React.FC<IBenchmarkProps> = ({
  isSingle = false,
  defaultSelectedBenchmarks,
  onSelectedBenchmarksChange,
  userIsPrime = true,
  customBenchmarks,
  customBenchmarksEnabled = false,
  onUpdateCustomBenchmark,
  // benchmarkOptions,
}) => {
  const options: BenchmarkOption[] = React.useMemo(() => {
    const base = BENCHMARKS_SELECT;

    return base.map((opt) => ({
      ...opt,
      disabled: !userIsPrime && !FREE_BENCHMARKS.includes(opt.value as any),
    }));
  }, [userIsPrime]);

  const { selected, toggle, isSelected } = useControlledState(
    defaultSelectedBenchmarks,
    isSingle
  );

  // Debounce selected values with 300ms delay
  const debouncedSelected = useDebounce(selected, 500);

  // Propagate changes upwards
  React.useEffect(() => {
    if (isSingle) {
      onSelectedBenchmarksChange(debouncedSelected[0] as any);
    } else {
      onSelectedBenchmarksChange(debouncedSelected as any);
    }
  }, [debouncedSelected, isSingle]);

  const buttonLabel = React.useMemo(() => {
    if (isSingle) {
      const opt = [...options, ...(customBenchmarks || [])].find(
        (o) => o.value === selected[0]
      );
      return opt?.label ?? 'Indicador';
    }
    return 'Indicadores';
  }, [isSingle, selected, options, customBenchmarks]);

  const [open, setOpen] = React.useState(false);

  if (isSingle) {
    return (
      <SelectApp
        value={selected[0] ?? ''}
        onChange={(e: any) => {
          toggle(e.target.value);
        }}
        options={[...(customBenchmarks ?? []), ...options]}
        startAdornment="Indicador:"
        className="w-fit"
      />
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('min-w-[140px] font-medium gap-1')}
        >
          {isSingle && (
            <span className="text-[#A0A0A0] text-sm font-base">
              Indicador:&nbsp;
            </span>
          )}
          {buttonLabel}
          {open ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-4 w-64">
        <div className="flex flex-col gap-4 max-h-96 overflow-y-auto">
          {/* Custom benchmarks first */}
          {customBenchmarks?.length ? (
            <ul className="space-y-1">
              {customBenchmarks.map((opt) => (
                <li
                  key={opt.value}
                  className="flex items-center justify-between cursor-pointer hover:bg-accent rounded-sm px-1.5 py-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(opt.value);
                  }}
                >
                  <span className="text-primary">{opt.label}</span>
                  {onUpdateCustomBenchmark && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpdateCustomBenchmark();
                        setOpen(false);
                      }}
                      className="p-1 hover:bg-muted rounded"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : customBenchmarksEnabled ? (
            <Button
              variant="ghost"
              size="sm"
              className="justify-start gap-1"
              onClick={() => {
                onUpdateCustomBenchmark?.();
                setOpen(false);
              }}
            >
              <Plus className="h-4 w-4" /> Criar benchmark da carteira
            </Button>
          ) : null}

          {/* Default benchmarks list */}
          <ul className="space-y-1 overflow-y-auto">
            {options.map((opt) => (
              <BenchmarkItem
                key={opt.value}
                option={opt}
                multiple={!isSingle}
                isSelected={isSelected(opt.value)}
                onSelect={() => {
                  toggle(opt.value);
                }}
              />
            ))}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
};
